import { useImage } from '@24i/nxg-sdk-gluons/src/context/ImageService';
import { Asset, ASSET_TYPE } from '@24i/nxg-sdk-photon';
import { Image, Interactable, View, ThemeIcon } from '@24i/nxg-sdk-quarks';
import AssetLabel from '@24i/nxg-sdk-smartott/src/components/AssetLabel';
import React from 'react';
import { useTranslation } from 'react-i18next';
import TitleView, { getTitleViewStyles } from '../../../../TitleView';
import { getSliderTestId, SLIDER_TEST_IDS } from '../../../test-utils';
import { useStore } from '../../../../../context/ApplicationStore';
import { SliderItemComponentProps } from '../../../types';
import { useTopRowDimensions } from '../../../../../hooks/useTopRowDimensions/useTopRowDimensions';

// Helper function to generate metadata for episodes
const getMetadataForEpisode = (item, t, subtitle) => {
    // Guard against item being undefined
    if (!item) {
        return { metadata: subtitle || '', title: '' };
    }

    if (item?.type !== ASSET_TYPE.EPISODE) {
        return { metadata: subtitle, title: item?.title || '' };
    }

    const { seasonNumber, episodeNumber, title } = item;
    const assetTitle = item?.seriesName || item?.podcastName;

    const seasonNumberText = seasonNumber
        ? `${t('asset.series.seasonInitial')}${seasonNumber} `
        : '';
    const episodeNumberText = episodeNumber
        ? `${t('asset.series.episode.initial')}${episodeNumber} `
        : '';
    const episodeTitleText = (seasonNumber || episodeNumber) && title ? `- ${title}` : title;

    return {
        metadata: `${seasonNumberText}${episodeNumberText}${episodeTitleText}`,
        title: assetTitle || '',
    };
};

// Helper function to render geo block icon
const renderGeoBlockIcon = (styles): JSX.Element => (
    <View style={styles.geoBlockIconContainer}>
        <ThemeIcon iconName="onLabel-geoblocked" size={16} />
    </View>
);

// Helper function to create image rendering function
const createImageRenderer = (sliderIndex, index) => {
    const ImageRenderer = ({ index: imageIndex, item: imageItem, style, source }) => (
        <Image
            testID={getSliderTestId(SLIDER_TEST_IDS.SLIDE_IMAGE, {
                scopeIndex: sliderIndex,
                index,
            })}
            priority={imageIndex <= 1} // preload first two slider images
            alt={imageItem?.title || ''}
            style={style}
            source={source}
            imagePosition="top"
        />
    );

    // Add display name to the component
    ImageRenderer.displayName = 'SliderImageRenderer';

    return ImageRenderer;
};

const Item = (props: SliderItemComponentProps & { subtitle?: string }) => {
    const {
        styles,
        data: item,
        onPress: onItemPress,
        index,
        sliderIndex,
        isObscured,
        showMetadata = true,
        isGeoBlocked,
        subtitle,
        hiddenHeroGradient = false,
    } = props;

    const { getObscuredImage, getImageUrl } = useImage();
    const { t } = useTranslation();
    const { runtimeConfig } = useStore();
    const { isLandscape } = useTopRowDimensions();
    const config = runtimeConfig;

    // Guard against undefined items
    if (!item) {
        return null;
    }

    const getImageUrlForAsset = (asset: Asset): string => {
        const { heroLandscape, heroPortrait, background, poster, packshotLandscape } = asset;
        return !isLandscape
            ? heroPortrait || heroLandscape || poster || packshotLandscape || background || ''
            : heroLandscape || packshotLandscape || background || poster || heroPortrait || '';
    };

    const imageUrl = getImageUrlForAsset(item);

    const enableDynamicSliderImageType = config?.app?.rows?.enableDynamicSliderImageType;
    const shouldEnableDynamicSliderImage = enableDynamicSliderImageType && item?.poster;

    // Get image sources
    const image = item?.heroLandscape || item?.still || item?.background || '';
    const obscuredImg = getObscuredImage(item);

    // Create render function
    const renderImage = createImageRenderer(sliderIndex, index);

    // Extract metadata and title processing
    const { metadata: metadata1, title: assetTitle } = getMetadataForEpisode(item, t, subtitle);

    return (
        <Interactable
            testID={getSliderTestId(SLIDER_TEST_IDS.SLIDE_CONTAINER_PRESSABLE, {
                index,
                scopeIndex: sliderIndex,
            })}
            key={item.id}
            style={styles.slideContainer}
            onPress={() => onItemPress && onItemPress()}
        >
            {isObscured && (
                <View
                    style={{
                        ...styles.imageStyle,
                        ...((!hiddenHeroGradient || assetTitle) && styles.gradient),
                        ...{ justifyContent: 'center', alignItems: 'center' },
                    }}
                >
                    {renderImage({
                        index,
                        item,
                        style: [styles.imageStyle, styles.dynamicPosterImage],
                        source: obscuredImg,
                    })}
                    <ThemeIcon iconName="onPrimary-lock" size={88} />
                    {isGeoBlocked ? renderGeoBlockIcon(styles) : undefined}
                </View>
            )}

            {!!image && !isObscured && (
                <>
                    <View
                        style={{
                            ...styles.imageStyle,
                            ...((!hiddenHeroGradient || assetTitle) && styles.gradient),
                        }}
                    />
                    <View style={styles.imageStyleContainer}>
                        {renderImage({
                            index,
                            item,
                            style: [
                                styles.imageStyle,
                                shouldEnableDynamicSliderImage && styles.dynamicLandscapeImage,
                            ],
                            source: getImageUrl(
                                {
                                    imagePath: imageUrl,
                                    ...(shouldEnableDynamicSliderImage
                                        ? {
                                              width: 1300,
                                              height: 730,
                                          }
                                        : { quality: 50 }),
                                },
                                item?.images?.heroLandscape
                                    ? item?.images?.heroLandscape
                                    : item?.images?.still
                            ),
                        })}
                        {shouldEnableDynamicSliderImage &&
                            renderImage({
                                index,
                                item,
                                style: [styles.imageStyle, styles.dynamicPosterImage],
                                source: getImageUrl(
                                    {
                                        imagePath: imageUrl,
                                        width: 375,
                                        height: 667,
                                    },
                                    item?.images?.poster
                                ),
                            })}
                    </View>
                    {isGeoBlocked ? renderGeoBlockIcon(styles) : undefined}
                </>
            )}

            <View testID={`Slider.SwiperSlide.${index}.View.1`} style={styles.textHolder}>
                {!!item.assetLabel && (
                    <View style={styles.assetLabelContainer}>
                        <AssetLabel text={item.assetLabel} />
                    </View>
                )}
                {showMetadata && (
                    <TitleView
                        title={isObscured ? t('asset.blocked.title') : assetTitle || ''}
                        channelLogo={item.channelLogo}
                        startsAt={item?.startsAt}
                        endsAt={item?.endsAt}
                        subtitle={metadata1}
                        isInCarousel
                        styles={(propsTheme) => ({
                            ...getTitleViewStyles(propsTheme, 'secondaryColor'),
                            holder: styles.titleViewHolder,
                        })}
                        variant="secondaryColor"
                        subtitleFontSize="h5"
                    />
                )}
            </View>
        </Interactable>
    );
};

Item.displayName = 'SliderItem';

export default Item;
